'use client';

import { c as _c } from "react/compiler-runtime";
import { tryCatch, withTimeout } from '@repo/utils';
import { useCallback, useMemo } from 'react';
import { getChainId } from 'viem/actions';
import { useClient, useConnectorClient, useSwitchChain } from 'wagmi';
import { useIsSafeAccount } from '../../../hooks/safe-wallet.hooks';
import { useWalletSupportsAtomicBatching } from '../../../hooks/useWalletSupportsAtomicBatching';
import { useUserAccount } from '../../../providers/UserAccountProvider';
import { classifyTransactionError, getChainShortName, isChainId } from '../../../utils';
import { ActionFlowError } from '../types.action-flow';

// viem's `custom` transport sets no timeout, so a wedged provider never answers.
const CHAIN_READ_TIMEOUT_MS = 3_000;
export function useActionFlowWagmiState(chainId, t0) {
  const $ = _c(20);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isFlowPrepared: t2
  } = t1;
  const isFlowPrepared = t2 === undefined ? false : t2;
  const {
    isLoading: isUserAccountLoading,
    userAddress
  } = useUserAccount();
  const isSafeWallet = useIsSafeAccount();
  let t3;
  if ($[2] !== chainId || $[3] !== userAddress) {
    t3 = {
      chainId,
      assertChainId: false,
      account: userAddress
    };
    $[2] = chainId;
    $[3] = userAddress;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: walletClient,
    isLoading: isWalletClientLoading
  } = useConnectorClient(t3);
  let t4;
  if ($[5] !== chainId) {
    t4 = {
      chainId
    };
    $[5] = chainId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const publicClient = useClient(t4);
  const {
    switchChainAsync
  } = useSwitchChain();
  let t5;
  if ($[7] !== isFlowPrepared) {
    t5 = {
      enabled: isFlowPrepared
    };
    $[7] = isFlowPrepared;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: walletSupportsAtomicBatching,
    error: capabilitiesError,
    isLoading: isCapabilitiesLoading
  } = useWalletSupportsAtomicBatching(walletClient?.account.address, chainId, t5);
  const isLoading = isWalletClientLoading || isCapabilitiesLoading || isUserAccountLoading;
  let t6;
  if ($[9] !== chainId || $[10] !== isSafeWallet || $[11] !== switchChainAsync || $[12] !== walletClient) {
    t6 = async () => {
      if (!walletClient) {
        throw new ActionFlowError("Wallet connection lost. Reconnect and try again.", {
          errorType: "chain-read-error"
        });
      }
      const readChainId = async () => {
        const {
          data,
          error
        } = await tryCatch(withTimeout(getChainId(walletClient), CHAIN_READ_TIMEOUT_MS));
        if (error) {
          throw new ActionFlowError("Your wallet didn't respond. Unlock it and try again.", {
            errorType: "chain-read-error",
            cause: error
          });
        }
        return data;
      };
      if ((await readChainId()) === chainId) {
        return;
      }
      const targetName = isChainId(chainId) ? getChainShortName(chainId) : `network ${chainId}`;
      if (isSafeWallet) {
        throw new ActionFlowError(`Your Safe is on the wrong network. Switch to ${targetName} in the Safe app and try again.`, {
          errorType: "chain-switch-error"
        });
      }
      const wrongNetwork = cause => new ActionFlowError(`Your wallet is on the wrong network. Switch it to ${targetName} and try again.`, {
        errorType: "chain-switch-error",
        cause
      });
      const {
        error: switchError
      } = await tryCatch(switchChainAsync({
        chainId
      }));
      if (switchError) {
        if (classifyTransactionError(switchError.message) === "user_rejected") {
          throw switchError;
        }
        throw wrongNetwork(switchError);
      }
      if ((await readChainId()) === chainId) {
        return;
      }
      throw wrongNetwork();
    };
    $[9] = chainId;
    $[10] = isSafeWallet;
    $[11] = switchChainAsync;
    $[12] = walletClient;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const ensureCorrectChain = t6;
  const t7 = capabilitiesError ? false : walletSupportsAtomicBatching ?? false;
  let t8;
  if ($[14] !== ensureCorrectChain || $[15] !== isLoading || $[16] !== publicClient || $[17] !== t7 || $[18] !== walletClient) {
    t8 = {
      walletClient,
      publicClient,
      walletSupportsAtomicBatching: t7,
      ensureCorrectChain,
      isLoading
    };
    $[14] = ensureCorrectChain;
    $[15] = isLoading;
    $[16] = publicClient;
    $[17] = t7;
    $[18] = walletClient;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}